import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import { Check, ArrowRight, CircleNotch, Star } from '@phosphor-icons/react';

const PLANS = [
  {
    key: 'basic',
    name: 'Basic',
    price: 0,
    period: 'Free forever',
    tagline: 'Get started with zero risk',
    popular: false,
    features: [
      'Answer enquiries automatically',
      '100 credits included monthly',
      '1 user',
      'Community support',
    ],
    cta: 'Get Started Free',
  },
  {
    key: 'professional',
    name: 'Professional',
    price: 39,
    period: '/month',
    tagline: 'For growing businesses',
    popular: false,
    features: [
      'Everything in Basic',
      '2,000 credits per month',
      '30 voice minutes included',
      'Advanced task engine (Orion)',
      'Priority email support',
    ],
    cta: 'Start Professional',
  },
  {
    key: 'teams',
    name: 'Teams',
    price: 99,
    period: '/month',
    tagline: 'For teams that move fast',
    popular: true,
    features: [
      'Everything in Professional',
      '6,000 credits per month',
      '100 voice minutes included',
      'Up to 10 team members',
      'Priority support',
    ],
    cta: 'Start Teams',
  },
  {
    key: 'partner_pro',
    name: 'Partner Pro',
    price: 299,
    period: '/month',
    tagline: 'For agencies & power users',
    popular: false,
    features: [
      'Everything in Teams',
      '20,000 credits per month',
      '300 voice minutes included',
      'Multi-tenant management',
      'Bring your own LLM keys',
      'Agent Fabric - External agent management',
      'Business analysis and reporting',
      'Dedicated support',
    ],
    cta: 'Start Partner Pro',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: null,
    period: '',
    tagline: 'Custom plans for complex needs',
    popular: false,
    features: [
      'Custom credit & voice allowances',
      'Dedicated account manager',
      'Onboarding & training included',
      'Custom integrations & SLAs',
      'Volume discounts',
    ],
    cta: 'Contact Us',
  },
];

const CREDIT_PACKS = [
  { key: 'credits_1000', credits: '1,000', price: 29, highlight: false },
  { key: 'credits_5000', credits: '5,000', price: 99, highlight: true },
  { key: 'credits_20000', credits: '20,000', price: 299, highlight: false },
];

const VOICE_PACKS = [
  { key: 'voice_100', minutes: '100', price: 19, highlight: false },
  { key: 'voice_500', minutes: '500', price: 79, highlight: true },
  { key: 'voice_1500', minutes: '1,500', price: 199, highlight: false },
];

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [processingPlan, setProcessingPlan] = useState(null);
  const [processingAddon, setProcessingAddon] = useState(null);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. No charges were made.');
    }
  }, [searchParams]);

  const handlePlanAction = async (planKey) => {
    if (planKey === 'basic') {
      navigate(isAuthenticated ? '/dashboard' : '/register');
      return;
    }
    if (!isAuthenticated) {
      sessionStorage.setItem('intended_plan', planKey);
      navigate('/register');
      return;
    }
    setProcessingPlan(planKey);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/checkout/subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_key: planKey, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error(data.error || 'Could not start checkout');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleAddonPurchase = async (addonType, addonKey) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setProcessingAddon(addonKey);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/checkout/addon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ addon_type: addonType, addon_key: addonKey, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error(data.error || 'Could not start checkout');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setProcessingAddon(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#121820]">
      <SEO
        title="Pricing - Simple Plans for Every Team | Man & Machine"
        description="Choose a plan that fits. Start free, upgrade when you're ready."
        canonicalUrl="https://manmachine.agency/pricing"
      />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight" data-testid="pricing-hero-title">
              Simple pricing,{' '}
              <span className="text-[#00E5C0]">real results</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#8B95A5] max-w-2xl mx-auto leading-relaxed">
              Start free. Pay only when you need more. No surprises, no hidden fees.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="pb-24" data-testid="pricing-plans-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {PLANS.map((plan, i) => (
              <motion.div
                key={plan.key}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                data-testid={`pricing-card-${plan.key}`}
                className={`relative flex flex-col rounded-2xl p-6 transition-all ${
                  plan.popular
                    ? 'bg-[#141A23] border-2 border-[#00E5C0]/30 shadow-[0_0_40px_rgba(0,229,192,0.06)]'
                    : 'bg-[#111820] border border-[rgba(255,255,255,0.08)]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00E5C0] text-[#0A0E14] text-xs font-semibold" data-testid="popular-badge">
                      <Star size={12} weight="fill" /> Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                  <p className="text-xs text-[#7D8896] mt-1">{plan.tagline}</p>
                </div>

                <div className="mb-6">
                  {plan.price === 0 ? (
                    <div className="text-4xl font-bold text-white">Free</div>
                  ) : plan.price === null ? (
                    <div className="text-4xl font-bold text-white">Custom</div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">${plan.price}</span>
                      <span className="text-sm text-[#7D8896]">{plan.period}</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Check size={15} className="text-[#00E5C0] mt-0.5 shrink-0" weight="bold" />
                      <span className="text-sm text-[#C0C8D4]">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => plan.key === 'enterprise' ? navigate('/contact') : handlePlanAction(plan.key)}
                  disabled={processingPlan === plan.key}
                  data-testid={`pricing-cta-${plan.key}`}
                  className={`w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-[#00E5C0] text-[#0A0E14] hover:bg-[#00CCB0]'
                      : 'bg-[rgba(255,255,255,0.05)] text-white border border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.08)]'
                  } ${processingPlan === plan.key ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {processingPlan === plan.key ? (
                    <><CircleNotch size={16} className="animate-spin" /> Processing...</>
                  ) : (
                    <>{plan.cta} <ArrowRight size={14} /></>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top-Up Section */}
      <section className="pb-24" data-testid="topup-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Need more usage?{' '}
              <span className="text-[#00E5C0]">Top up anytime</span>
            </h2>
            <p className="mt-3 text-sm text-[#7D8896]">
              Buy extra credits or voice minutes whenever you need them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Credit Packs */}
            <div>
              <h3 className="text-sm font-semibold text-[#8B95A5] uppercase tracking-wider mb-4">Credit Packs</h3>
              <div className="space-y-3">
                {CREDIT_PACKS.map((pack) => (
                  <div
                    key={pack.key}
                    data-testid={`addon-${pack.key}`}
                    className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                      pack.highlight
                        ? 'bg-[#141A23] border border-[#00E5C0]/20'
                        : 'bg-[#111820] border border-[rgba(255,255,255,0.08)]'
                    }`}
                  >
                    <div>
                      <span className="text-white font-semibold text-sm">{pack.credits} credits</span>
                      {pack.highlight && (
                        <span className="ml-2 text-[10px] font-semibold text-[#00E5C0] bg-[rgba(0,229,192,0.08)] px-2 py-0.5 rounded-full">
                          Best value
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddonPurchase('credit_pack', pack.key)}
                      disabled={processingAddon === pack.key}
                      data-testid={`buy-${pack.key}`}
                      className="text-sm font-semibold text-[#00E5C0] hover:text-[#00CCB0] transition-colors flex items-center gap-1"
                    >
                      {processingAddon === pack.key ? (
                        <CircleNotch size={14} className="animate-spin" />
                      ) : (
                        <>${pack.price}</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Voice Packs */}
            <div>
              <h3 className="text-sm font-semibold text-[#8B95A5] uppercase tracking-wider mb-4">Voice Minutes</h3>
              <div className="space-y-3">
                {VOICE_PACKS.map((pack) => (
                  <div
                    key={pack.key}
                    data-testid={`addon-${pack.key}`}
                    className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                      pack.highlight
                        ? 'bg-[#141A23] border border-[#00E5C0]/20'
                        : 'bg-[#111820] border border-[rgba(255,255,255,0.08)]'
                    }`}
                  >
                    <div>
                      <span className="text-white font-semibold text-sm">{pack.minutes} minutes</span>
                      {pack.highlight && (
                        <span className="ml-2 text-[10px] font-semibold text-[#00E5C0] bg-[rgba(0,229,192,0.08)] px-2 py-0.5 rounded-full">
                          Best value
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddonPurchase('voice_pack', pack.key)}
                      disabled={processingAddon === pack.key}
                      data-testid={`buy-${pack.key}`}
                      className="text-sm font-semibold text-[#00E5C0] hover:text-[#00CCB0] transition-colors flex items-center gap-1"
                    >
                      {processingAddon === pack.key ? (
                        <CircleNotch size={14} className="animate-spin" />
                      ) : (
                        <>${pack.price}</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Common questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'What are credits?',
                a: 'Credits power your AI workers. Each action your workers take uses a small number of credits. Your plan includes monthly credits, and you can buy more anytime.',
              },
              {
                q: 'Can I change plans?',
                a: 'Yes. Upgrade or downgrade at any time. Changes take effect at the start of your next billing cycle.',
              },
              {
                q: 'What happens when I run out of credits?',
                a: "Your workers will pause. You can top up instantly to resume, or wait for your next monthly refresh.",
              },
              {
                q: 'Do unused credits roll over?',
                a: 'Monthly plan credits reset each cycle. Top-up credits never expire.',
              },
            ].map((faq, i) => (
              <div key={i} className="bg-[#111820] border border-[rgba(255,255,255,0.08)] rounded-xl p-5" data-testid={`faq-${i}`}>
                <h3 className="text-sm font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-sm text-[#8B95A5] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to get started?</h2>
          <p className="text-sm text-[#7D8896] mb-8">Start free. No credit card required.</p>
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
            data-testid="pricing-final-cta"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[#00E5C0] text-[#0A0E14] font-semibold text-sm hover:bg-[#00CCB0] transition-colors"
          >
            Get Started Free <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
